import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, Star, Gift, Sparkles, X, Heart } from "lucide-react";
import { RevealText, Reveal } from "@/components/Reveal";
import Img from "@/components/Img";
import { IMAGES, CATEGORIES, TESTIMONIALS, BOOKING_STATUS, GIFT_CARDS, REVEAL_REEL, MINI_SESSIONS } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];
const WORDS = ["treasured", "printed", "felt", "framed"];

const EXPERIENCE_COPY = [
  "A photo session with me isn't about stiff poses and forced smiles; it's a celebration of your authentic, beautiful moments. Whether it's a giggling toddler running free, sweet nose kisses with your partner, or the way your baby fits perfectly into your arms, I'm here to capture the little things that hold the greatest significance.",
  "From the moment you book, I'm your hype girl, planning partner, and cheerleader. We'll share laughs and have fun, making the entire experience feel as effortless as enjoying a coffee date with a friend (bonus points if you bring iced coffee!). Don't worry about feeling awkward—I'll guide you every step of the way to ensure you feel comfortable and confident in front of the camera. By the end of our session, you'll likely forget that you're even being photographed.",
  "And the most exciting part? When you view the final images, you'll experience the joy, love, and connection all over again, bringing those cherished moments back to life.",
];

// Three featured session images beneath the logo
const FEATURED = [
  { src: "/home-1.jpg", label: "Families", to: "/portfolio?cat=families" },
  { src: "/home-maternity.jpg", label: "Maternity", to: "/portfolio?cat=maternity" },
  { src: "/home-fresh48.jpg", label: "Fresh 48", to: "/portfolio?cat=fresh48" },
];

const Hero = () => {
  const [wi, setWi] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setWi((v) => (v + 1) % WORDS.length), 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      data-testid="home-hero"
      className="relative bg-cream pt-4 md:pt-6 pb-20 md:pb-24 overflow-hidden"
    >
      {/* Three featured images */}
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
          {FEATURED.map((f, i) => {
            const isThird = i === 2;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + i * 0.12, duration: 0.9, ease: EASE }}
                className={isThird ? "col-span-2 md:col-span-1" : ""}
              >
                <Link
                  to={f.to}
                  data-testid={`home-featured-${f.label.toLowerCase()}`}
                  className="group block relative overflow-hidden"
                >
                  <div
                    className={
                      isThird
                        ? "aspect-[3/2] md:aspect-[3/4] overflow-hidden"
                        : "aspect-[3/4] overflow-hidden"
                    }
                  >
                    <img
                      src={f.src}
                      alt={f.label}
                      className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
                      loading="eager"
                    />
                  </div>
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-cream translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                    <p className="font-serif italic text-2xl md:text-3xl">{f.label}</p>
                    <ArrowUpRight size={22} className="mb-1" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Tagline + CTA under the images */}
        <div className="mt-14 md:mt-20 text-center max-w-5xl mx-auto">
          {BOOKING_STATUS.visible !== false && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.7, ease: EASE }}
              className="mb-8 flex justify-center"
            >
              <Link
                to={BOOKING_STATUS.to}
                data-testid="hero-booking-badge"
                className="group inline-flex items-center gap-3 rounded-full border border-sage/30 bg-sagelight/50 px-5 py-2 text-sm hover:border-sage hover:bg-sagelight transition-colors duration-300"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-sage opacity-70 animate-ping"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-sage"></span>
                </span>
                <span className="overline text-sage">{BOOKING_STATUS.label}</span>
                <span className="text-inksoft font-serif italic">— {BOOKING_STATUS.detail}</span>
              </Link>
            </motion.div>
          )}
          <motion.h1
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
            className="font-serif font-light text-ink leading-[1.25] tracking-tight text-2xl sm:text-3xl md:text-[2.15rem] lg:text-[2.4rem] max-w-4xl mx-auto"
          >
            Portrait and lifestyle photography rooted in genuine connection and authentic
            moments, creating memories to be
            <span className="block mt-2 overflow-hidden pb-[0.15em]">
              <AnimatePresence mode="wait">
                <motion.span
                  key={WORDS[wi]}
                  initial={{ y: "60%", opacity: 0 }}
                  animate={{ y: "0%", opacity: 1 }}
                  exit={{ y: "-60%", opacity: 0 }}
                  transition={{ duration: 0.55, ease: EASE }}
                  className="inline-block italic text-sage text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-none"
                >
                  {WORDS[wi]}.
                </motion.span>
              </AnimatePresence>
            </span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-3"
          >
            <Link
              to="/investment"
              data-testid="hero-book-btn"
              className="group inline-flex items-center gap-3 rounded-full bg-ink text-cream px-8 py-4 text-sm font-medium hover:bg-sage transition-colors duration-300"
            >
              Book your session
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/client-love"
              data-testid="hero-client-love-btn"
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 bg-cream/70 px-8 py-4 text-sm font-medium text-ink backdrop-blur hover:border-ink transition-colors duration-300"
            >
              Client love
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.9 }}
            className="mt-12 flex flex-col items-center"
            data-testid="home-hero-stats"
          >
            <div className="flex items-start justify-center gap-10 sm:gap-16">
              <div className="flex flex-col items-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-sagelight/70 ring-1 ring-sage/25">
                  <Star size={20} fill="currentColor" strokeWidth={0} className="text-sage" />
                </div>
                <p className="mt-3 font-serif text-2xl md:text-3xl text-ink leading-none">5.0</p>
                <p className="mt-1.5 text-[10px] tracking-[0.22em] uppercase text-inksoft">Client rating</p>
              </div>

              <div className="h-16 w-px bg-ink/10 mt-1" aria-hidden="true" />

              <div className="flex flex-col items-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-sagelight/70 ring-1 ring-sage/25">
                  <Heart size={18} strokeWidth={1.6} className="text-sage" />
                </div>
                <p className="mt-3 font-serif text-2xl md:text-3xl text-ink leading-none">500+</p>
                <p className="mt-1.5 text-[10px] tracking-[0.22em] uppercase text-inksoft">Families served</p>
              </div>
            </div>

            <p className="mt-8 font-serif italic text-inksoft text-base md:text-lg text-center max-w-lg">
              Your preferred Acadiana photographer
              <span className="mx-3 inline-block h-1.5 w-1.5 rounded-full bg-sage align-middle" aria-hidden="true" />
              Studio &amp; Outdoor sessions
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const RevealReel = () => (
  <section data-testid="home-reveal-reel" className="pt-16 md:pt-24 pb-20 md:pb-28 bg-cream">
    <div className="mx-auto max-w-[1400px] px-6 md:px-10 mb-8 md:mb-10 flex items-end justify-between gap-6">
      <div>
        <p className="overline text-sage mb-3">{REVEAL_REEL.eyebrow}</p>
        <h2 className="font-serif font-light text-ink leading-[0.98] tracking-tight text-3xl md:text-5xl">
          A slow scroll of recent<br className="hidden md:block" /> favorites
        </h2>
      </div>
      <Link
        to="/portfolio"
        data-testid="reel-view-all-link"
        className="hidden md:inline-flex group items-center gap-2 text-ink text-sm font-medium border-b border-ink/30 pb-1 hover:border-sage hover:text-sage transition-colors whitespace-nowrap"
      >
        See the full portfolio
        <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </Link>
    </div>
    <Marquee speed={28} gradient={false} pauseOnHover autoFill>
      {REVEAL_REEL.images.map((src, i) => (
        <div key={i} className="mx-2 md:mx-3 h-[340px] md:h-[420px] overflow-hidden bg-stone2">
          <img
            src={src}
            alt="Recent session favorite"
            loading="lazy"
            className="h-full w-auto block object-cover hover:scale-[1.03] transition-transform duration-700 ease-out"
          />
        </div>
      ))}
    </Marquee>
  </section>
);

const MarqueeStrip = () => (
  <div data-testid="home-marquee" className="border-y border-ink/10 bg-cream py-6 md:py-7">
    <Marquee speed={38} gradient={false} autoFill>
      {["Studio", "Families", "Maternity", "Couples", "Branding", "Seniors", "Lifestyle Newborn", "Boudoir"].map(
        (w, i) => (
          <span key={i} className="mx-3 md:mx-4 font-sans uppercase tracking-[0.22em] text-xs md:text-sm text-ink/70">
            {w}
            <span className="text-sage mx-3 md:mx-4">•</span>
          </span>
        )
      )}
    </Marquee>
  </div>
);

const HOME_FACTS = [
  "Words of Affirmation? That's my love language. So please, whatever you do, don't leave me on read!",
  "I'm an iced coffee junkie and a die-hard fan of Dr. Pepper AND Coke (don't make me choose).",
  "Type A personality over here — I've got 3 to-do lists, a planner, and 4 calendars to prove it.",
  "Country music is always playing while I'm editing. (Carrie, Luke, Cody — you name it!)",
];

const Manifesto = () => (
  <section data-testid="home-manifesto" className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
      <Reveal>
        <p className="overline text-sage mb-5">I'm your new photographer &amp; bestest pal</p>
        <h2 className="font-serif font-light text-ink leading-[0.95] tracking-tight text-4xl md:text-6xl">
          But that's not why you're here.
        </h2>
        <div className="mt-10 space-y-7">
          {HOME_FACTS.map((f, i) => (
            <div key={i} className="border-b border-ink/10 pb-6 last:border-0">
              <p className="font-serif italic text-2xl md:text-3xl text-sage">
                Interesting fact #{i + 1}
              </p>
              <p className="mt-2 text-inksoft leading-relaxed max-w-lg">{f}</p>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="relative mx-auto aspect-square w-[85%] max-w-[460px]">
          <div className="absolute inset-0 rounded-full border-2 border-dashed border-sage/40" />
          <div className="absolute -right-2 -top-2 h-14 w-14 rounded-sm bg-clay/70 hidden md:block" />
          <div className="absolute inset-5 overflow-hidden rounded-full bg-sagelight">
            <img
              src="/jac-facts.jpg"
              alt="Jacqueline Claire holding a 'book that photoshoot' sign"
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const PortfolioTeaser = () => (
  <section data-testid="home-portfolio-teaser" className="bg-stone2 py-28 md:py-40">
    <div className="mx-auto max-w-[1400px] px-6 md:px-10">
      <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14 md:mb-20">
        <div>
          <p className="overline text-sage mb-5">Selected work</p>
          <h2 className="font-serif font-light text-ink leading-[0.95] tracking-tight text-5xl md:text-7xl">
            Every kind of story
          </h2>
        </div>
        <Link
          to="/portfolio"
          data-testid="teaser-view-all-link"
          className="group inline-flex items-center gap-2 text-ink text-sm font-medium border-b border-ink/30 pb-1 w-fit hover:border-sage hover:text-sage transition-colors"
        >
          View full portfolio
          <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </Reveal>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
        {CATEGORIES.slice(0, 4).map((c, i) => (
          <Reveal key={c.key} delay={i * 0.08}>
            <Link to={`/portfolio?cat=${c.key}`} data-testid={`teaser-cat-${c.key}`} className="group block">
              <Img
                src={c.img}
                alt={c.title}
                className={`${i % 2 === 0 ? "aspect-[3/4]" : "aspect-[3/4] md:mt-12"}`}
                imgClassName="group-hover:scale-105"
              />
              <p className="mt-4 font-serif text-2xl text-ink">{c.title}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section data-testid="home-testimonials" className="mx-auto max-w-[900px] px-6 md:px-10 py-20 md:py-28 text-center">
    <Reveal>
      <p className="overline text-sage mb-8">Kind words</p>
    </Reveal>
    <div className="space-y-14 md:space-y-20">
      {TESTIMONIALS.map((t, i) => (
        <Reveal key={i} delay={0.05}>
          <blockquote className="font-serif font-light italic text-ink text-xl md:text-3xl leading-[1.35]">
            “{t.quote}”
          </blockquote>
          <p className="mt-5 overline text-inkmuted">— {t.name}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const GiftCardsCta = () => (
  <section data-testid="home-gift-cards" className="mx-auto max-w-[1400px] px-6 md:px-10 py-28 md:py-40">
    <Reveal>
      <div className="relative overflow-hidden bg-ink text-cream px-8 md:px-20 py-20 md:py-28">
        <div className="absolute -top-24 -right-16 h-80 w-80 rounded-full bg-sage/25 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-16 h-72 w-72 rounded-full bg-sage/20 blur-3xl pointer-events-none" />
        <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-8">
            <div className="flex items-center gap-3 mb-8">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-cream/10 text-cream">
                <Gift size={18} strokeWidth={1.6} />
              </span>
              <p className="overline text-sage">{GIFT_CARDS.eyebrow}</p>
            </div>
            <h2 className="font-serif font-light text-cream text-4xl md:text-6xl leading-[1.05] tracking-tight mb-6">
              {GIFT_CARDS.title}
            </h2>
            <p className="text-cream/75 leading-relaxed text-lg max-w-xl">
              {GIFT_CARDS.body}
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <a
              href={GIFT_CARDS.ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-gift-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-cream text-ink px-9 py-4 text-sm font-medium hover:bg-sagelight transition-colors duration-300"
            >
              {GIFT_CARDS.ctaLabel}
              <ArrowUpRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
          </div>
        </div>
      </div>
    </Reveal>
  </section>
);

const ExperienceTeaser = () => (
  <section data-testid="home-experience" className="bg-sagelight py-28 md:py-40">
    <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
      <Reveal className="md:col-span-5" y={60}>
        <Img
          src="/home-experience.jpg"
          alt="A close-up of an engaged couple holding hands"
          className="aspect-[4/5] rounded-tr-[5rem]"
          imgClassName="hover:scale-105"
        />
      </Reveal>
      <Reveal className="md:col-span-7" delay={0.1}>
        <p className="overline text-sagedark mb-5">The experience</p>
        <h2 className="font-serif font-light text-ink text-5xl md:text-6xl leading-[0.98] tracking-tight">
          More coffee date than photo shoot
        </h2>
        <div className="mt-8 space-y-5 text-inksoft leading-relaxed max-w-2xl">
          {EXPERIENCE_COPY.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
        <Link
          to="/experience"
          data-testid="home-experience-btn"
          className="group mt-10 inline-flex items-center gap-3 rounded-full bg-ink text-cream px-9 py-4 text-sm font-medium hover:bg-sage transition-colors duration-300"
        >
          Discover the experience
          <ArrowUpRight
            size={17}
            className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </Link>
      </Reveal>
    </div>
  </section>
);

const MiniSessionsPopup = () => {
  const [open, setOpen] = useState(false);
  const hasSessions = MINI_SESSIONS.sessions && MINI_SESSIONS.sessions.length > 0;
  const signature = hasSessions
    ? MINI_SESSIONS.sessions.map((s) => `${s.theme}|${s.date}|${s.href || ""}`).join("__")
    : "";
  const storageKey = `jc_minis_seen:${signature}`;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!hasSessions) return;
    if (sessionStorage.getItem(storageKey) === "1") return;
    const t = setTimeout(() => setOpen(true), 1600);
    return () => clearTimeout(t);
  }, [hasSessions, storageKey]);

  // Lock body scroll while popup is open
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!hasSessions) return null;

  const close = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(storageKey, "1");
    } catch (e) {}
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="mini-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: EASE }}
          className="fixed inset-0 z-[80] bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4 md:p-6"
          onClick={close}
          data-testid="mini-sessions-backdrop"
        >
          <motion.div
            key="mini-card"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.45, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-cream text-ink shadow-2xl max-h-[90vh] overflow-y-auto overscroll-contain"
            data-testid="mini-sessions-popup"
            data-lenis-prevent
          >
            <button
              onClick={close}
              aria-label="Close"
              data-testid="mini-sessions-close"
              className="absolute top-4 right-4 z-10 h-9 w-9 inline-flex items-center justify-center rounded-full bg-cream/95 backdrop-blur-sm hover:bg-ink/5 transition-colors"
            >
              <X size={18} strokeWidth={1.6} />
            </button>

            <div className="px-8 md:px-10 pt-12 pb-10">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={14} strokeWidth={1.6} className="text-sage" />
                <p className="overline text-sage">{MINI_SESSIONS.eyebrow}</p>
              </div>
              <h3 className="font-serif font-light text-ink text-3xl md:text-4xl leading-[1.05] tracking-tight mb-8">
                {MINI_SESSIONS.title}
              </h3>
              <ul className="divide-y divide-ink/10 border-y border-ink/10 mb-6 max-h-[280px] overflow-y-auto overscroll-contain">
                {MINI_SESSIONS.sessions.map((s, i) => (
                  <li
                    key={s.theme + i}
                    className="flex items-center justify-between gap-4 py-4"
                    data-testid={`mini-session-row-${i}`}
                  >
                    <div className="min-w-0">
                      <p className="font-serif italic text-lg md:text-xl text-ink leading-tight">
                        {s.theme}
                      </p>
                      <p className="text-xs tracking-[0.14em] uppercase text-inksoft mt-1">
                        {s.date}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      {s.href && s.status && (
                        <span className="text-xs tracking-[0.14em] uppercase text-inkmuted whitespace-nowrap">
                          {s.status}
                        </span>
                      )}
                      {s.href ? (
                        <a
                          href={s.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={close}
                          data-testid={`mini-session-book-${i}`}
                          className="group inline-flex items-center gap-1.5 rounded-full bg-ink text-cream px-5 py-2 text-xs tracking-[0.14em] uppercase hover:bg-sage transition-colors duration-300 whitespace-nowrap"
                        >
                          {s.bookLabel || MINI_SESSIONS.bookLabel}
                          <ArrowUpRight
                            size={13}
                            className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                          />
                        </a>
                      ) : (
                        <span className="text-xs tracking-[0.14em] uppercase text-sage whitespace-nowrap">
                          {s.status || "Coming soon"}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <button
                onClick={close}
                data-testid="mini-sessions-dismiss"
                className="text-xs tracking-[0.14em] uppercase text-inksoft hover:text-ink transition-colors"
              >
                Maybe later
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default function Home() {
  return (
    <div>
      <Hero />
      <RevealReel />
      <Manifesto />
      <PortfolioTeaser />
      <MarqueeStrip />
      <ExperienceTeaser />
      <Testimonials />
      <GiftCardsCta />
      <MiniSessionsPopup />
    </div>
  );
}
